import { FilterDimension } from '@Portal/models/smartFilters/FilterDimension';
import { SmartFilter } from '@Portal/models/smartFilters/SmartFilter';
import { SmartFilterDataType } from '@Portal/models/smartFilters/SmartFilterDataType';
import { SmartFilterSelectionViewModel } from '@Portal/models/smartFilters/SmartFilterSelectionViewModel';
import { ViewConfiguration, getSmartFilters } from '@Shared/api/smartFilters';
import { OrderBy } from '@Shared/types/portal/OrderBy';
import { OrderByOperator } from '@Shared/types/portal/OrderByOperator';
import { SmartDataSelectionViewModel } from '@Shared/types/portal/SmartDataSelectionViewModel';
import { defineStore } from 'pinia';

export const useFilterStore = defineStore(
  'filter',
  () => {
    const availableFilterDimensions = ref<FilterDimension[]>([]);

    // secondary are visual applied filters and are differentiated since:
    // - they don't show in the UI
    // - visuals show a clear button
    // - they don't impact what filters you can apply
    const groupedSelectedFilters = ref<{
      primary: Record<string, SmartFilter[]>;
      secondary: Record<string, SmartFilter[]>;
    }>({ primary: {}, secondary: {} });
    const groupedSelectedPrimaryFilterKeyCount = computed(() => {
      return [...Object.values(groupedSelectedFilters.value.primary)].filter((f) => f.length > 0).length;
    });

    const dateFilter = ref<SmartFilter | null>(null);
    // Tracks whether the user (or defaults) have ever initialised the date filter in this session.
    // false = first visit, apply page defaults; true = keep whatever dateFilter currently is (including null).
    const dateFilterInitialized = ref(false);
    // Set to true by Filters.vue once its full onMounted initialisation sequence completes.
    // Components like TableView gate their initial data load on this flag to avoid a flash
    // caused by intermediate filter states during initialisation.
    const filtersReady = ref(false);
    const searchFilter = ref<SmartFilter | null>(null);
    const selectedPrimaryFilters = computed<SmartFilter[]>(() => {
      const filters = [...Object.values(groupedSelectedFilters.value.primary)].flat();
      if (dateFilter.value) filters.push(dateFilter.value);
      if (searchFilter.value) filters.push(searchFilter.value);
      return filters;
    });
    const selectedSecondaryFilters = computed<SmartFilter[]>(() => {
      return [...Object.values(groupedSelectedFilters.value.secondary)].flat();
    });

    const selectedStandardFilterKey = ref<string>();
    // Only used in mobile to display search filter input
    const isSearchFilterSelected = ref(false);
    const isStandardFilterSelected = ref(false);
    const orderBy = ref<OrderBy | null>(null);

    const availableStandardFilterDimensions = computed(() =>
      availableFilterDimensions.value.filter(
        (dim) =>
          ![
            SmartFilterDataType.DateTime,
            SmartFilterDataType.DateTimePeriod,
            SmartFilterDataType.Search,
            SmartFilterDataType.SearchAll,
          ].includes(dim.filterDataType)
      )
    );
    const availableDateFilterDimensions = computed(() =>
      availableFilterDimensions.value.filter((dim) => [SmartFilterDataType.DateTime].includes(dim.filterDataType))
    );
    const availableSearchFilterDimensions = computed(() =>
      availableFilterDimensions.value.filter((dim) =>
        [SmartFilterDataType.Search, SmartFilterDataType.SearchAll].includes(dim.filterDataType)
      )
    );

    const removeFilters = (filters: SmartFilter[], isPrimary = true) => {
      for (const filter of filters) removeFilter(filter, isPrimary);
    };

    const getGroupedFilters = (isPrimary: boolean) =>
      isPrimary ? groupedSelectedFilters.value.primary : groupedSelectedFilters.value.secondary;

    const removeFilter = (filter: SmartFilter, isPrimary = true) => {
      // secondary doesn't support date or search filters

      // Date Filter
      if (isPrimary && isDateFilter(filter)) {
        dateFilter.value = null;
        dateFilterInitialized.value = true;
        return;
      }

      // Search Filter
      if (isPrimary && isSearchFilter(filter)) {
        searchFilter.value = null;
        return;
      }

      const filters = getGroupedFilters(isPrimary);

      if (filters[filter.filterKey]) {
        const index = filters[filter.filterKey].findIndex((f) => f.filterValue == filter.filterValue);
        // trying to remove a filter that doesn't exist just ignores it
        if (index >= 0) filters[filter.filterKey].splice(index, 1);
      }
    };

    const clearPrimaryStandardFilters = () => {
      groupedSelectedFilters.value.primary = {};
    };

    const clearAllPrimaryFilters = () => {
      clearPrimaryStandardFilters();
      dateFilter.value = null;
      searchFilter.value = null;
    };

    const clearAllSecondaryFilters = () => {
      groupedSelectedFilters.value.secondary = {};
    };

    const clearDateFilter = (isAutoClear = false) => {
      dateFilter.value = null;
      dateFilterInitialized.value = !isAutoClear;
    };

    const isDateFilter = (filter: SmartFilter) =>
      [SmartFilterDataType.DateTime, SmartFilterDataType.DateTimePeriod].includes(filter.filterDataType);

    const isSearchFilter = (filter: SmartFilter) =>
      [SmartFilterDataType.Search, SmartFilterDataType.SearchAll].includes(filter.filterDataType);

    // secondary doesn't support date or search filters
    const addOrUpdateFilter = (filters: SmartFilter[], removeFiltersForKey?: string, isPrimary = true) => {
      const groupedFilters = getGroupedFilters(isPrimary);
      if (removeFiltersForKey) groupedFilters[removeFiltersForKey] = [];

      for (const filter of filters) {
        // Date Filter
        if (isPrimary && isDateFilter(filter)) {
          dateFilter.value = filter;
          dateFilterInitialized.value = true;
          continue;
        }

        // Search Filter
        if (isPrimary && isSearchFilter(filter)) {
          searchFilter.value = filter;
          continue;
        }

        let existingFilters = groupedFilters[filter.filterKey];

        if (!existingFilters) {
          groupedFilters[filter.filterKey] = existingFilters = [filter];
          continue;
        }

        // we don't already have this filter value
        if (!existingFilters.some((f) => f.filterValue == filter.filterValue)) existingFilters.push(filter);
      }
    };

    /**
     * Applies a set of default filters while respecting the user's existing date filter selection.
     *
     * When calling after clearAllPrimaryFilters(), pass the pre-clear state as the optional params
     * so the snapshot isn't lost. When calling on mount (no prior clear), omit them and the current
     * store state is used directly.
     *
     * - Active date filter → preserve it, apply only non-date defaults
     * - Initialized but null → user deliberately cleared; apply only non-date defaults
     * - Not yet initialized → first visit; apply all defaults including date
     */
    const applyDefaultFilters = (
      defaults: SmartFilter[],
      savedDateFilter?: SmartFilter | null,
      isDateFilterInitialized?: boolean
    ) => {
      const dateToUse = savedDateFilter !== undefined ? savedDateFilter : dateFilter.value;
      const isInitialized =
        isDateFilterInitialized !== undefined ? isDateFilterInitialized : dateFilterInitialized.value;

      if (dateToUse) {
        addOrUpdateFilter([dateToUse, ...defaults.filter((f) => !isDateFilter(f))]);
      } else if (isInitialized) {
        addOrUpdateFilter(defaults.filter((f) => !isDateFilter(f)));
      } else {
        addOrUpdateFilter(defaults);
      }
    };

    const toggleOrderBy = (key: string) => {
      if (!orderBy.value || orderBy.value.fieldKey != key) {
        orderBy.value = {
          fieldKey: key,
          operatorAsString: OrderByOperator.ASC,
        };
      } else if (orderBy.value.operatorAsString == OrderByOperator.ASC) {
        orderBy.value.operatorAsString = OrderByOperator.DESC;
      } else {
        orderBy.value.operatorAsString = OrderByOperator.ASC;
      }
    };

    let loadFiltersController: AbortController | null = null;

    const loadFilters = async (
      smartDataModuleId: string,
      filterTags: string[],
      viewConfiguration?: ViewConfiguration
    ) => {
      loadFiltersController?.abort();
      loadFiltersController = new AbortController();
      const signal = loadFiltersController.signal;

      const selection = new SmartFilterSelectionViewModel(selectedPrimaryFilters.value);
      selection.smartDataTags = filterTags;
      try {
        const filters = await getSmartFilters(smartDataModuleId, selection, viewConfiguration, signal);
        availableFilterDimensions.value = filters;
      } catch (e: any) {
        if (e?.code === 'ERR_CANCELED') return;
        throw e;
      }
    };

    const getSmartDataSelection = (isPrimary: boolean) => {
      const result: SmartDataSelectionViewModel = {
        smartFilters: isPrimary ? selectedPrimaryFilters.value : selectedSecondaryFilters.value,
      };
      if (orderBy.value) {
        result.orderByFields = [orderBy.value.fieldKey];
        result.orderByOperator = orderBy.value.operatorAsString;
      }
      return result;
    };

    const smartDataSelection = computed(() => getSmartDataSelection(true));
    // used by visuals this isn't included in filters calls (and is a separate data call)
    const secondarySmartDataSelection = computed(() => getSmartDataSelection(false));

    // used for titles in the dashboard tiles
    const dateFilterDisplayValue = computed<string>(() => {
      if (!dateFilter.value || !dateFilter.value.filterValue) {
        return 'for All Time';
      } else if (dateFilter.value.filterValue == 'Today' || dateFilter.value.filterValue.startsWith('This')) {
        return 'for ' + dateFilter.value.filterValue;
      } else if (dateFilter.value.filterValue == 'Yesterday' || dateFilter.value.filterValue.startsWith('Last')) {
        return 'from ' + dateFilter.value.filterValue;
      } else if (dateFilter.value.filterDataType == SmartFilterDataType.DateTime) {
        return 'from ' + dateFilter.value.filterValue + ' to ' + dateFilter.value.filterValueUpperBound;
      } else {
        return dateFilter.value.filterValue;
      }
    });

    return {
      availableFilterDimensions,
      availableStandardFilterDimensions,
      availableDateFilterDimensions,
      availableSearchFilterDimensions,

      selectedPrimaryFilters,
      selectedSecondaryFilters,
      groupedSelectedFilters,
      groupedSelectedPrimaryFilterKeyCount,
      dateFilter,
      dateFilterInitialized,
      dateFilterDisplayValue,
      filtersReady,
      searchFilter,
      selectedStandardFilterKey,
      isSearchFilterSelected,
      isStandardFilterSelected,
      orderBy,
      smartDataSelection,
      secondarySmartDataSelection,

      toggleOrderBy,
      addOrUpdateFilter,
      applyDefaultFilters,
      loadFilters,
      removeFilter,
      removeFilters,
      clearPrimaryStandardFilters,
      clearAllPrimaryFilters,
      clearAllSecondaryFilters,
      clearDateFilter,
      isDateFilter,
    };
  },
  {
    persist: {
      paths: ['dateFilterInitialized'],
      storage: sessionStorage,
    },
  }
);
